import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./AjusteMensalPage.module.css";
import {
  aprovarAjusteMensalGeral,
  atualizarAjusteMensalLinha,
  getAjusteMensalPlano,
} from "../planejamentomensal/api";

const WEEK_IDS = [1, 2, 3, 4, 5];
const pad2 = (n) => String(n).padStart(2, "0");
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};

function monthLabel(value) {
  if (!value) return "";
  const [y, m] = value.split("-");
  if (!y || !m) return value;
  try {
    const dt = new Date(Number(y), Number(m) - 1, 1);
    const label = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(dt);
    return label.charAt(0).toUpperCase() + label.slice(1);
  } catch {
    return value;
  }
}

function readQueryMonth(search) {
  const params = new URLSearchParams(search || "");
  const value = String(params.get("month") || "").trim();
  return /^\d{4}-\d{2}$/.test(value) ? value : "";
}

function parseTempoToMinutes(raw) {
  const str = String(raw || "").trim();
  if (!str) return 0;
  const match = str.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (!match) return 0;
  const hours = Number(match[1]) || 0;
  const minutes = Number(match[2]) || 0;
  return Math.max(0, hours * 60 + minutes);
}

function normalizeTempoHHMM(raw) {
  const str = String(raw || "").trim();
  if (!str) return "";
  const match = str.match(/^(\d{1,2}):(\d{1,2})(?::\d{1,2})?$/);
  if (!match) return "";
  const hours = Math.max(0, Number(match[1]) || 0);
  const minutes = Math.min(59, Math.max(0, Number(match[2]) || 0));
  return `${pad2(hours)}:${pad2(minutes)}`;
}

function formatMinutes(totalMinutes) {
  const mins = Math.max(0, Math.round(Number(totalMinutes) || 0));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

function normalizeTimeTyping(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function normalizeTimeBlur(raw) {
  const str = String(raw || "").trim();
  if (!str) return "";
  return formatMinutes(parseTempoToMinutes(str));
}

function normalizeQtyTyping(raw) {
  return String(raw || "").replace(/\D/g, "").slice(0, 8);
}

function rowKey(row) {
  return JSON.stringify([row.gerencia || "", row.coordenador || "", row.atividade || "", row.unidade || ""]);
}

function buildDraftFromRow(row) {
  const draft = {};
  for (const week of WEEK_IDS) {
    const sem = row?.semanas?.[week];
    draft[week] = {
      quantidade: sem?.quantidade != null && sem?.quantidade !== "" ? String(sem.quantidade) : "",
      tempo: sem?.tempo ? normalizeTempoHHMM(sem.tempo) : "",
    };
  }
  return draft;
}

export default function AjusteMensalPage({ onBack }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [month, setMonth] = useState(() => readQueryMonth(location.search) || currentMonth());
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");
  const [rows, setRows] = useState([]);
  const [busyRow, setBusyRow] = useState("");
  const [editingRow, setEditingRow] = useState("");
  const [rowDrafts, setRowDrafts] = useState({});
  const [approvingGeral, setApprovingGeral] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const out = await getAjusteMensalPlano({ month });
      setRows(Array.isArray(out?.rows) ? out.rows : []);
      setError("");
    } catch {
      setError("Falha ao carregar os dados do ajuste mensal.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    const fromQuery = readQueryMonth(location.search);
    if (fromQuery && fromQuery !== month) {
      setMonth(fromQuery);
    }
  }, [location.search, month]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setEditingRow("");
    setRowDrafts({});
    setFeedback("");
  }, [month]);

  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    navigate("/");
  };

  const ajusteRows = useMemo(() => {
    const map = new Map();

    for (const row of rows || []) {
      const semana = Number(row?.semana);
      if (!WEEK_IDS.includes(semana)) continue;

      const gerencia = String(row?.gerencia || "").trim();
      const coordenador = String(row?.coordenador || "").trim();
      const atividade = String(row?.atividade || "").trim();
      const grupo = String(row?.grupo || "").trim();
      const unidade = String(row?.unidade || "").trim();
      if (!coordenador || !atividade) continue;

      const key = JSON.stringify([gerencia, coordenador, atividade, grupo, unidade]);
      if (!map.has(key)) {
        map.set(key, {
          gerencia,
          coordenador,
          atividade,
          grupo,
          unidade,
          status_linha: "ATIVO",
          aprovado_geral_em: null,
          semanas: {},
        });
      }

      const item = map.get(key);
      item.status_linha = String(row?.status_linha || item.status_linha || "ATIVO").trim().toUpperCase();
      item.aprovado_geral_em = row?.aprovado_geral_em || item.aprovado_geral_em || null;
      item.semanas[semana] = {
        quantidade: row?.quantidade == null || row?.quantidade === "" ? "" : String(Number(row?.quantidade) || 0),
        tempo: normalizeTempoHHMM(row?.tempo || row?.tempo_min || ""),
      };
    }

    const list = Array.from(map.values()).map((item) => {
      let quantidadeTotal = 0;
      let tempoTotalMin = 0;

      for (const week of WEEK_IDS) {
        const sem = item.semanas[week] || { quantidade: "", tempo: "" };
        const quantidade = Number(sem.quantidade || 0);
        quantidadeTotal += quantidade;
        tempoTotalMin += quantidade * parseTempoToMinutes(sem.tempo);
      }

      return {
        ...item,
        isRecusada: item.status_linha === "RECUSADO",
        isAprovadaGeral: !!item.aprovado_geral_em,
        quantidadeTotal,
        tempoTotalMin,
      };
    });

    return list.sort((a, b) => {
      const g = String(a.gerencia).localeCompare(String(b.gerencia), "pt-BR");
      if (g) return g;
      const c = String(a.coordenador).localeCompare(String(b.coordenador), "pt-BR");
      if (c) return c;
      return String(a.atividade).localeCompare(String(b.atividade), "pt-BR");
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return ajusteRows;
    return ajusteRows.filter((row) => {
      const hay = `${row.gerencia} ${row.coordenador} ${row.atividade} ${row.grupo} ${row.unidade}`.toLowerCase();
      return hay.includes(q);
    });
  }, [ajusteRows, query]);

  const resumo = useMemo(() => {
    let quantidadeTotal = 0;
    let tempoTotalMin = 0;

    for (const row of filteredRows) {
      if (row.isRecusada) continue;
      quantidadeTotal += row.quantidadeTotal || 0;
      tempoTotalMin += row.tempoTotalMin || 0;
    }

    return {
      linhas: filteredRows.length,
      recusadas: filteredRows.filter((r) => r.isRecusada).length,
      quantidadeTotal,
      tempoTotalMin,
    };
  }, [filteredRows]);

  const pendentesAprovacaoGeral = useMemo(() => {
    return ajusteRows.filter((row) => !row.isRecusada && !row.isAprovadaGeral).length;
  }, [ajusteRows]);

  const updateDraftValue = (key, semana, field, value) => {
    setRowDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [semana]: {
          ...((prev[key] || {})[semana] || { quantidade: "", tempo: "" }),
          [field]: value,
        },
      },
    }));
  };

  const handleRecusar = async (row) => {
    const key = rowKey(row);
    if (!month || busyRow) return;

    const status_linha = row.isRecusada ? "ATIVO" : "RECUSADO";
    setBusyRow(key);
    setError("");
    setFeedback("");
    try {
      const out = await atualizarAjusteMensalLinha({
        month,
        gerencia: row.gerencia,
        coordenador: row.coordenador,
        atividade: row.atividade,
        unidade: row.unidade || null,
        grupo: row.grupo || null,
        status_linha,
        semanas: [],
      });
      if (!out?.ok) {
        setError(out?.message || "Falha ao atualizar o status da linha.");
        return;
      }
      if (editingRow === key) setEditingRow("");
      setFeedback(status_linha === "RECUSADO" ? "Linha recusada no ajuste." : "Linha reativada no ajuste.");
      await loadRows();
    } catch {
      setError("Falha ao atualizar o status da linha.");
    } finally {
      setBusyRow("");
    }
  };

  const handleStartEdit = (row) => {
    const key = rowKey(row);
    if (row.isRecusada || row.isAprovadaGeral) return;
    setEditingRow(key);
    setError("");
    setFeedback("");
    setRowDrafts((prev) => ({
      ...prev,
      [key]: prev[key] || buildDraftFromRow(row),
    }));
  };

  const handleCancelEdit = () => {
    setEditingRow("");
    setError("");
  };

  const handleSalvarEdicao = async (row) => {
    const key = rowKey(row);
    if (!month || busyRow) return;

    const draft = rowDrafts[key] || buildDraftFromRow(row);
    const semanas = WEEK_IDS.map((semana) => {
      const sem = draft?.[semana] || { quantidade: "", tempo: "" };
      return {
        semana,
        quantidade: String(sem.quantidade || "").trim(),
        tempo: normalizeTimeBlur(sem.tempo || ""),
      };
    });

    setBusyRow(key);
    setError("");
    setFeedback("");
    try {
      const out = await atualizarAjusteMensalLinha({
        month,
        gerencia: row.gerencia,
        coordenador: row.coordenador,
        atividade: row.atividade,
        unidade: row.unidade || null,
        grupo: row.grupo || null,
        status_linha: "ATIVO",
        semanas,
      });
      if (!out?.ok) {
        setError(out?.message || "Falha ao salvar a edicao do ajuste.");
        return;
      }
      setEditingRow("");
      setFeedback("Edicao salva no ajuste mensal.");
      await loadRows();
    } catch {
      setError("Falha ao salvar a edicao do ajuste.");
    } finally {
      setBusyRow("");
    }
  };

  const handleAprovarGeral = async () => {
    if (!month || approvingGeral || !pendentesAprovacaoGeral) return;

    setApprovingGeral(true);
    setError("");
    setFeedback("");
    try {
      const out = await aprovarAjusteMensalGeral({ month });
      if (!out?.ok) {
        setError(out?.message || "Falha na aprovacao geral do ajuste.");
        return;
      }
      const qs = new URLSearchParams({ month }).toString();
      navigate(`/programacoes/planejamento-mensal/contrato?${qs}`);
    } catch {
      setError("Falha na aprovacao geral do ajuste.");
    } finally {
      setApprovingGeral(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        <aside className={styles.sidebar}>
          <button className={styles.backBtn} type="button" onClick={goBack}>
            Voltar ao menu principal
          </button>

          <div className={styles.card}>
            <h1>Ajuste mensal</h1>
            <p>Ajuste das linhas aprovadas no planejamento mensal, com recusa ou edicao.</p>
          </div>

          <div className={styles.card}>
            <label className={styles.field}>
              <span>Mes de referencia</span>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            </label>
            <label className={styles.field}>
              <span>Buscar</span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Gerencia, coordenador ou atividade"
              />
            </label>
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}>
              <span>Competencia</span>
              <strong>{monthLabel(month)}</strong>
            </div>
            <div className={styles.stat}>
              <span>Linhas</span>
              <strong>{resumo.linhas}</strong>
            </div>
            <div className={styles.stat}>
              <span>Recusadas</span>
              <strong>{resumo.recusadas}</strong>
            </div>
            <div className={styles.stat}>
              <span>Quantidade total (ativas)</span>
              <strong>{resumo.quantidadeTotal}</strong>
            </div>
            <div className={styles.stat}>
              <span>Horas totais (Qtd x Hora)</span>
              <strong>{formatMinutes(resumo.tempoTotalMin)}</strong>
            </div>
            <div className={styles.stat}>
              <span>Pendentes de aprovacao geral</span>
              <strong>{pendentesAprovacaoGeral}</strong>
            </div>
          </div>
        </aside>

        <main className={styles.main}>
          <header className={styles.mainHead}>
            <div className={styles.mainHeadTop}>
              <div>
                <h2>Ajuste mensal</h2>
                <p>Recuse ou edite as linhas. Ao final, use a aprovacao geral para seguir ao contrato.</p>
              </div>
              <button
                type="button"
                className={styles.mainActionBtn}
                disabled={approvingGeral || !pendentesAprovacaoGeral}
                onClick={handleAprovarGeral}
              >
                {approvingGeral ? "Aprovando..." : "Aprovar ajuste geral"}
              </button>
            </div>
          </header>

          <section className={styles.panel}>
            {loading ? <div className={styles.feedback}>Carregando dados do ajuste mensal...</div> : null}
            {error ? <div className={`${styles.feedback} ${styles.feedbackError}`}>{error}</div> : null}
            {feedback ? <div className={`${styles.feedback} ${styles.feedbackOk}`}>{feedback}</div> : null}
            {!loading && !error && !filteredRows.length ? (
              <div className={styles.feedback}>Nenhum dado encontrado para os filtros informados.</div>
            ) : null}

            {!!filteredRows.length && (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Gerencia</th>
                      <th>Coordenador</th>
                      <th>Atividade</th>
                      <th>Unidade</th>
                      {WEEK_IDS.map((week) => (
                        <th key={week}>Sem {week}</th>
                      ))}
                      <th>Total</th>
                      <th>Acoes</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredRows.map((row) => {
                      const key = rowKey(row);
                      const isEditing = editingRow === key && !row.isRecusada && !row.isAprovadaGeral;
                      const draft = rowDrafts[key] || buildDraftFromRow(row);

                      return (
                        <tr
                          key={`${row.gerencia}__${row.coordenador}__${row.atividade}__${row.unidade}`}
                          className={`${row.isRecusada ? styles.rowRecusada : ""} ${row.isAprovadaGeral ? styles.rowAprovada : ""}`}
                        >
                          <td>{row.gerencia || "-"}</td>
                          <td>{row.coordenador || "-"}</td>
                          <td>{row.atividade || "-"}</td>
                          <td>{row.unidade || "-"}</td>

                          {WEEK_IDS.map((week) => {
                            const semDraft = draft?.[week] || { quantidade: "", tempo: "" };
                            const sem = row.semanas[week];

                            if (!sem && !isEditing) return <td key={week} className={styles.weekCellEmpty}>-</td>;

                            if (isEditing) {
                              return (
                                <td key={week}>
                                  <div className={styles.cellInputs}>
                                    <input
                                      className={styles.cellInput}
                                      type="text"
                                      inputMode="numeric"
                                      pattern="\d*"
                                      value={semDraft.quantidade}
                                      placeholder="Quantidade"
                                      onChange={(e) =>
                                        updateDraftValue(key, week, "quantidade", normalizeQtyTyping(e.target.value))
                                      }
                                    />
                                    <input
                                      className={styles.cellInput}
                                      type="text"
                                      inputMode="numeric"
                                      maxLength={5}
                                      value={semDraft.tempo}
                                      placeholder="hh:mm"
                                      onChange={(e) =>
                                        updateDraftValue(key, week, "tempo", normalizeTimeTyping(e.target.value))
                                      }
                                      onBlur={() =>
                                        updateDraftValue(key, week, "tempo", normalizeTimeBlur(semDraft.tempo))
                                      }
                                    />
                                  </div>
                                </td>
                              );
                            }

                            return (
                              <td key={week}>
                                <div className={styles.weekCell}>
                                  <span>Qtd: {sem.quantidade === "" ? 0 : sem.quantidade}</span>
                                  <span>Hora: {sem.tempo || "00:00"}</span>
                                </div>
                              </td>
                            );
                          })}

                          <td>
                            <div className={styles.totalCell}>
                              <span>Qtd: {row.quantidadeTotal}</span>
                              <span>Hora: {formatMinutes(row.tempoTotalMin)}</span>
                            </div>
                          </td>

                          <td className={styles.actionsCell}>
                            <div className={styles.actionsStack}>
                              <button
                                type="button"
                                className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                disabled={busyRow === key || row.isAprovadaGeral}
                                onClick={() => handleRecusar(row)}
                              >
                                {row.isRecusada ? "Reativar" : "Recusar"}
                              </button>

                              {isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    className={styles.actionBtn}
                                    disabled={busyRow === key}
                                    onClick={() => handleSalvarEdicao(row)}
                                  >
                                    Salvar edicao
                                  </button>
                                  <button
                                    type="button"
                                    className={`${styles.actionBtn} ${styles.actionBtnGhost}`}
                                    disabled={busyRow === key}
                                    onClick={handleCancelEdit}
                                  >
                                    Cancelar
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className={styles.actionBtn}
                                  disabled={busyRow === key || row.isRecusada || row.isAprovadaGeral}
                                  onClick={() => handleStartEdit(row)}
                                >
                                  {row.isAprovadaGeral ? "Aprovado geral" : "Editar"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}
